import React, { useEffect, useState } from "react";

/**
 * Inline approval card rendered when the AI calls `request_confirmation`.
 * Mirrors the UX of the cseditor's PendingActionCard:
 *   - 4 states: pending → working → resolved | rejected
 *   - Countdown timer to TTL expiry
 *   - Bullets + warnings rendered as separate lists
 *   - Approve/Reject buttons; result label after click
 */

const STATE_PENDING = "pending";
const STATE_WORKING = "working";
const STATE_RESOLVED = "resolved";
const STATE_REJECTED = "rejected";
const STATE_FAILED = "failed";

export default function GatewayAIConfirmCard({
  token,
  proposed = null,        // { summary: {title, target, bullets, warnings}, mutation }
  expiresAt,
  onApprove,              // async (token) => void
  onReject,               // async (token) => void
  initialState = STATE_PENDING,
  resolvedSummary = "",   // shown after a successful Approve
  resolvedError = "",     // shown after a failed Approve (returns to pending)
}) {
  const [state, setState] = useState(initialState);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntil(expiresAt));

  useEffect(() => {
    if (state !== STATE_PENDING || !expiresAt) return undefined;
    const id = setInterval(() => {
      const next = secondsUntil(expiresAt);
      setSecondsLeft(next);
      if (next <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [state, expiresAt]);

  const expired = secondsLeft <= 0;
  const summary = proposed?.summary || {};
  const mutation = proposed?.mutation || {};
  const isDestructive = /^(delete_|revoke_|remove_)/.test(mutation.kind || "");

  // Once the user clicks Approve/Reject we always advance to a final state.
  // The previous "back to PENDING" path was misleading — the token is
  // synchronously consumed inside the popover's handler before any work
  // happens, so a re-click would just produce another "expired" error.
  const handleApprove = async () => {
    if (state !== STATE_PENDING) return;
    setState(STATE_WORKING);
    setError(null);
    try {
      await onApprove(token);
      setState(STATE_RESOLVED);
    } catch (err) {
      setError(err?.message || String(err));
      setState(STATE_FAILED);
    }
  };

  const handleReject = async () => {
    if (state !== STATE_PENDING) return;
    setState(STATE_WORKING);
    setError(null);
    try {
      await onReject(token);
      setState(STATE_REJECTED);
    } catch (err) {
      // Reject failures are unusual (no API call) — still advance to a
      // final state rather than offering an apparent retry.
      setError(err?.message || String(err));
      setState(STATE_REJECTED);
    }
  };

  const headline = buildHeadline(summary, mutation);

  return (
    <div
      className={`gwAIConfirmCard gwAIConfirmCard--${state}${
        isDestructive ? " gwAIConfirmCard--destructive" : ""
      }`}
      data-token={token}
    >
      <div className="gwAIConfirmHeader">
        <span className="gwAIConfirmIcon" aria-hidden="true">
          {isDestructive ? "⚠" : "✦"}
        </span>
        <span className="gwAIConfirmTitle">{headline}</span>
        {state === STATE_PENDING && expiresAt ? (
          <span
            className={`gwAIConfirmTimer ${expired ? "is-expired" : ""}`}
            title={expired ? "This proposal has expired" : "Time left to approve"}
          >
            {expired ? "expired" : formatSeconds(secondsLeft)}
          </span>
        ) : null}
      </div>

      <div className="gwAIConfirmBody">
        {summary.target ? (
          <div className="gwAIConfirmTarget">{summary.target}</div>
        ) : null}
        {Array.isArray(summary.bullets) && summary.bullets.length > 0 ? (
          <ul className="gwAIConfirmBullets">
            {summary.bullets.map((b, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: renderInlineMd(b) }} />
            ))}
          </ul>
        ) : null}
        {Array.isArray(summary.warnings) && summary.warnings.length > 0 ? (
          <ul className="gwAIConfirmWarnings">
            {summary.warnings.map((w, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: renderInlineMd(w) }} />
            ))}
          </ul>
        ) : null}
      </div>

      {error && state === STATE_PENDING ? (
        <div className="gwAIConfirmError" role="alert">
          {error}
        </div>
      ) : null}

      <div className="gwAIConfirmActions">
        {state === STATE_PENDING ? (
          <>
            <button
              type="button"
              className="gwAIConfirmBtn gwAIConfirmBtn--reject"
              onClick={handleReject}
              disabled={expired}
            >
              Cancel
            </button>
            <button
              type="button"
              className="gwAIConfirmBtn gwAIConfirmBtn--approve"
              onClick={handleApprove}
              disabled={expired}
            >
              {expired ? "Expired" : isDestructive ? "Approve & run" : "Approve"}
            </button>
          </>
        ) : state === STATE_WORKING ? (
          <span className="gwAIConfirmStatus">
            <span className="gwAIConfirmSpinner" />
            Running…
          </span>
        ) : state === STATE_RESOLVED ? (
          <span className="gwAIConfirmStatus gwAIConfirmStatus--ok">
            ✓ {resolvedSummary || "Approved"}
          </span>
        ) : state === STATE_FAILED ? (
          <span className="gwAIConfirmStatus gwAIConfirmStatus--failed">
            ✕ {error || "Action failed"}
          </span>
        ) : (
          <span className="gwAIConfirmStatus gwAIConfirmStatus--rejected">
            ✕ Cancelled
          </span>
        )}
      </div>
      {resolvedError && state === STATE_PENDING ? (
        <div className="gwAIConfirmError" role="alert">
          {resolvedError}
        </div>
      ) : null}
    </div>
  );
}

function buildHeadline(summary, mutation) {
  if (summary?.title) {
    if (summary.target) return `${summary.title} ${summary.target}`;
    return summary.title;
  }
  if (mutation?.kind) return `AI wants to ${mutation.kind.replace(/_/g, " ")}`;
  return "AI requested an action";
}

function renderInlineMd(text) {
  if (typeof text !== "string") return "";
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

function secondsUntil(epoch) {
  if (!epoch) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((epoch - Date.now()) / 1000));
}

function formatSeconds(s) {
  if (!Number.isFinite(s)) return "";
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  }
  return `${s}s`;
}
